import React, { useMemo, useState } from 'react';

const EMAIL_ENDPOINT = () => {
  const recipient = process.env.REACT_APP_FORM_RECIPIENT || '';
  if (!recipient) return null;
  return `https://formsubmit.co/ajax/${encodeURIComponent(recipient)}`;
};

const nameValid = (v) => /^[A-Za-z .'-]{2,}$/.test(v) && !/\d/.test(v);
const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function WaitlistForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null); // {type:'success'|'error', msg:string}

  const endpoint = useMemo(EMAIL_ENDPOINT, []);

  const validate = () => {
    const e = {};
    if (!nameValid(name.trim())) {
      e.name = 'Enter a valid name with letters only. Spaces, apostrophes, and hyphens are allowed.';
    }
    if (!emailValid(email.trim())) {
      e.email = 'Enter a valid email address.';
    }
    if (!consent) {
      e.consent = 'Consent is required.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submitLead = async () => {
    if (!endpoint) {
      // no recipient configured — simulate success so local dev works
      await new Promise((r) => setTimeout(r, 400));
      return;
    }
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        name,
        email,
        _subject: 'New CareerMove Waitlist Signup',
        _template: 'table',
      }),
    });
    if (!res.ok) throw new Error('Network error');
    const data = await res.json();
    if (!data.success) throw new Error('Submission blocked');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (honeypot) return;
    if (!validate()) {
      setToast({ type: 'error', msg: 'Please correct the highlighted fields.' });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    try {
      setLoading(true);
      await submitLead();
      setName('');
      setEmail('');
      setConsent(false);
      setErrors({});
      setToast({ type: 'success', msg: 'You’re on the list. We’ll reach out when matching opens.' });
    } catch (err) {
      setToast({ type: 'error', msg: 'Could not submit right now. Try again in a moment.' });
    } finally {
      setLoading(false);
      setTimeout(() => setToast(null), 3500);
    }
  };

  return (
    <>
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

      {/* honeypot */}
      <input
        style={{ display: 'none' }}
        tabIndex={-1}
        autoComplete="off"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
      />

      <form onSubmit={onSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label className="label">Name</label>
            <input
              className={`input ${errors.name ? 'error' : ''}`}
              placeholder="Jane Smith"
              value={name}
              onChange={(e) => setName(e.target.value)}
              inputMode="text"
              pattern="^[A-Za-z .'-]+$"
              title="Letters, spaces, apostrophes, and hyphens only"
              required
            />
            {errors.name && <div className="error-text">{errors.name}</div>}
          </div>

          <div className="form-group">
            <label className="label">Email</label>
            <input
              type="email"
              className={`input ${errors.email ? 'error' : ''}`}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              inputMode="email"
              required
            />
            {errors.email && <div className="error-text">{errors.email}</div>}
          </div>
        </div>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            required
          />
          I agree to be contacted about CareerMove updates.
        </label>
        {errors.consent && <div className="error-text">{errors.consent}</div>}

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 10 }}>
          <button className="btn" type="submit" disabled={loading}>
            {loading ? 'Submitting…' : 'Join the waitlist'}
          </button>
          <span className="small">No spam. Unsubscribe anytime.</span>
        </div>
      </form>
    </>
  );
}
